import { JSX, useCallback, useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import {
  getCountryCoverage,
  type CountryCoverageResponse,
} from "@/services/parasiteRateService"
import CoverageHistogram from "./CoverageHistogram"

interface CoverageCacheEntry {
  countryId: string
  version: string
  data: CountryCoverageResponse
}

export interface CoverageHistogramPanelProps {
  /**
   * ISO3 country id of the selected data point, or null when nothing is
   * selected. The panel renders nothing (and fires no fetch) when this is null.
   */
  countryId: string | null
  /** Country name, forwarded to the full-page dialog title. */
  countryName?: string | null
  version: string
  getAccessToken: () => Promise<string>
  /** Active date-range start (year decimal), or null. Dims out-of-range years. */
  rangeStart: number | null
  /** Active date-range end (year decimal), or null. Dims out-of-range years. */
  rangeEnd: number | null
}

/**
 * CoverageHistogramPanel renders the body of the PR coverage card: a histogram
 * of the selected point's country PR coverage (surveys / literature review,
 * per start year), plus loading and error states. It owns the on-demand
 * fetch and renders nothing while no data point with a country is selected.
 *
 * Coverage is fetched on demand when a country becomes selected and cached for
 * only the most recently viewed country+version.
 *
 * @param props - See CoverageHistogramPanelProps.
 * @returns The coverage histogram body, or null when nothing is selected.
 */
export default function CoverageHistogramPanel({
  countryId,
  countryName = null,
  version,
  getAccessToken,
  rangeStart,
  rangeEnd,
}: CoverageHistogramPanelProps): JSX.Element | null {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [coverage, setCoverage] = useState<CountryCoverageResponse | null>(null)

  // Single-entry cache of the last fetched country+version. A ref (not state)
  // because reading or overwriting it must not itself trigger a re-render.
  const cacheRef = useRef<CoverageCacheEntry | null>(null)

  // Identifies the most recent fetch so a slow earlier request cannot overwrite
  // the state with stale data after a newer country was requested.
  const latestRequestRef = useRef<number>(0)

  /**
   * Load coverage for the given country+version, preferring the one-entry cache
   * and otherwise fetching fresh.
   *
   * @param targetCountryId - ISO3 id of the country to load coverage for.
   */
  const loadCoverage = useCallback(
    async (targetCountryId: string): Promise<void> => {
      const cached: CoverageCacheEntry | null = cacheRef.current
      if (
        cached != null &&
        cached.countryId === targetCountryId &&
        cached.version === version
      ) {
        setCoverage(cached.data)
        setError(null)
        setIsLoading(false)
        return
      }

      const requestId: number = latestRequestRef.current + 1
      latestRequestRef.current = requestId

      setIsLoading(true)
      setError(null)
      setCoverage(null)

      try {
        const token: string = await getAccessToken()
        const data: CountryCoverageResponse = await getCountryCoverage(
          token,
          targetCountryId,
          version,
        )

        if (requestId !== latestRequestRef.current) {
          return
        }

        cacheRef.current = {
          countryId: targetCountryId,
          version,
          data,
        }
        setCoverage(data)
      } catch (err) {
        if (requestId !== latestRequestRef.current) {
          return
        }
        const message: string =
          err instanceof Error ? err.message : "Failed to load coverage"
        console.error("CoverageHistogramPanel: coverage load failed:", err)
        setError(message)
      } finally {
        if (requestId === latestRequestRef.current) {
          setIsLoading(false)
        }
      }
    },
    [version, getAccessToken],
  )

  useEffect(() => {
    if (countryId == null) {
      return
    }
    void loadCoverage(countryId)
  }, [countryId, loadCoverage])

  if (countryId == null) {
    return null
  }

  return (
    <div className="flex flex-col gap-2">
      {isLoading && (
        <div
          className="flex items-center gap-2 py-6 text-xs text-muted-foreground"
          role="status"
        >
          <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          Loading coverage…
        </div>
      )}

      {!isLoading && error != null && (
        <p className="py-4 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {!isLoading && error == null && coverage != null && (
        <CoverageHistogram
          coverage={coverage}
          countryName={countryName}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
        />
      )}
    </div>
  )
}
